import { FC } from 'react';
import { Box, Tooltip } from '@mui/material';

export interface MeetingStartButtonProps {
  /** Zoom-Raum des Termins. Fehlt er, wird der Knopf DISABLED gezeigt — nie versteckt. */
  zoomLink?: string | null;
  /**
   * Zusaetzlicher Sperrgrund, der den Knopf trotz vorhandenem Raum deaktiviert
   * (heute nur: offene Termin-Uebergabe). Der Text wandert unveraendert in den
   * Tooltip — Knopf-Zustand und Begruendung haben damit dieselbe Quelle.
   */
  gesperrtGrund?: string | null;
  /** Kompakte Variante fuer die Verlaufszeilen. */
  kompakt?: boolean;
  'data-testid'?: string;
}

/** Tooltip-Wortlaut bei fehlendem Raum — Vorgabe Alex 06.08.2026. */
export const OHNE_ZOOM_HINWEIS = 'Kein Zoom-Link vorhanden';

const PlayIcon: FC<{ size: number }> = ({ size }) => (
  <svg width={size} height={size} viewBox="0 0 16 16" fill="currentColor">
    <rect x="1" y="3" width="9" height="10" rx="1.5" />
    <path d="M11 6l3.5-2v8L11 10V6z" />
  </svg>
);

/**
 * „Meeting starten" — Entscheid Alex 06.08.2026: der Knopf ist IMMER sichtbar.
 *
 * Kein Zeitfenster, keine Status-Bedingung. Auch am abgesagten, verschobenen
 * oder vergangenen Termin bleibt er stehen, solange ein Zoom-Raum am Termin
 * haengt — Zoom- und Google-Raum werden bei einer Absage bewusst nicht
 * geloescht (Invariante #114 §B), der Raum ist also real erreichbar.
 *
 * Fehlt der Raum wirklich, wird der Knopf DEAKTIVIERT gezeigt statt versteckt:
 * ein fehlender Knopf ist von einem fehlenden Recht nicht unterscheidbar, ein
 * grauer Knopf mit Grund schon.
 */
const MeetingStartButton: FC<MeetingStartButtonProps> = ({
  zoomLink,
  gesperrtGrund,
  kompakt = false,
  'data-testid': testId,
}) => {
  const raum = zoomLink?.trim();
  const grund = gesperrtGrund ?? (raum ? null : OHNE_ZOOM_HINWEIS);

  const basis = {
    display: 'flex',
    alignItems: 'center',
    // Design-Test Stufe 2 (25.10.2026): Schrift aus der Theme-Rolle;
    // Farbe seit Stufe 2b wieder Gruen wie Original.
    typography: 'label',
    gap: 2,
    color: 'primary.contrastText',
    borderRadius: '8px',
    px: kompakt ? 2 : 3,
    py: kompakt ? 1 : 2,
    flexShrink: 0,
  } as const;

  if (grund) {
    return (
      <Tooltip title={grund} placement="top">
        <Box
          component="span"
          data-testid={testId}
          aria-disabled="true"
          sx={{ ...basis, bgcolor: 'grey.200', opacity: 0.6, cursor: 'not-allowed' }}
        >
          <PlayIcon size={kompakt ? 12 : 14} />
          Starten
        </Box>
      </Tooltip>
    );
  }

  return (
    <Box
      component="button"
      type="button"
      data-testid={testId}
      onClick={() => window.open(raum!, '_blank', 'noopener,noreferrer')}
      sx={{
        ...basis,
        // Gruen der Status-Familie (theme.palette.status.green). Hover als
        // Helligkeitsstufe statt zweiter Farbe: eine rechnende Theme-Funktion
        // faellt in Tests mit nacktem `createTheme()` auf die Nase, ein
        // Token-String nicht.
        bgcolor: 'status.green.text',
        border: 'none',
        cursor: 'pointer',
        fontFamily: 'inherit',
        '&:hover': { filter: 'brightness(0.88)' },
        transition: 'filter 150ms',
      }}
    >
      <PlayIcon size={kompakt ? 12 : 14} />
      Starten
    </Box>
  );
};

export default MeetingStartButton;
